import {
	faBars,
	faChevronRight,
	faCircle,
	faEllipsis,
	faFont,
	faIcons,
	faPlus,
	faTag,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CSSProperties } from 'react';
import { useCallback, useRef, useState } from 'react';
import { Icon } from '../../../../../components/base/Icon';
import { Button } from '../../../../../components/forms/Button';
import { Tooltip } from '../../../../../components/overlays/Tooltip';
import { useThemeColors } from '../../../../../providers/ThemeProvider';
import { useTranslation } from '../../../../../providers/TranslationProvider';
import { cn } from '../../../../../utils/cn';
import { useComponentRegistry } from '../../../../registry/ComponentRegistry';
import type { FieldConfiguration } from '../../../../types';
import { DraggableList, DragHandle } from '../../../shared/DraggableList';

interface FieldsSidebarProps {
	fields: FieldConfiguration[];
	selectedFieldIndex: number | null;
	onSelectField: (index: number) => void;
	onAddField: () => void;
	onDeleteField: (index: number) => void;
	onReorderFields: (fields: FieldConfiguration[]) => void;
	onReceiveField?: (field: FieldConfiguration, targetIndex: number) => void;
	columnId: string;
	availableFields?: Array<{
		fieldName: string;
		fieldLabel: string;
		objectTable: string;
		objectLabel: string;
	}>;
	// When true the sidebar only allows clicking a field to view its config;
	// add / delete / drag-to-reorder are hidden. Click-to-select stays so the
	// caller can render the configurator in a disabled state on read-only
	// lists (built-in defaults, non-editable saved searches).
	isReadOnly?: boolean;
}

/** Shows a tooltip with the full field label only when the text is truncated */
function TruncatedFieldLabel({
	label,
	isSelected,
	field,
	previewStyle,
	colors,
}: {
	label: string;
	isSelected: boolean;
	field: FieldConfiguration;
	previewStyle: CSSProperties;
	colors: ReturnType<typeof useThemeColors>;
}) {
	const textRef = useRef<HTMLDivElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	const checkTruncation = useCallback(() => {
		const el = textRef.current;
		if (el != null) {
			setIsTruncated(el.scrollWidth > el.clientWidth);
		}
	}, []);

	return (
		<Tooltip
			content={label}
			position="top"
			disabled={!isTruncated}
			className="flex-1 min-w-0 text-left"
		>
			<div onMouseEnter={checkTruncation}>
				<div
					ref={textRef}
					className={cn(
						'text-[13px] truncate',
						isSelected
							? 'font-semibold'
							: `font-medium ${colors.classes.textSecondary}`,
					)}
				>
					{label}
				</div>

				{/* Mini preview of the style */}
				{field.displayStyle !== 'text' &&
					field.displayStyle !== 'line_break' && (
						<div className="mt-1">
							<span
								className={cn(
									'inline-block px-1.5 py-0.5 text-[10px] opacity-80',
									field.displayStyle === 'pill' ? 'rounded-full' : 'rounded',
								)}
								style={previewStyle}
							>
								Preview
							</span>
						</div>
					)}
			</div>
		</Tooltip>
	);
}

export function FieldsSidebar({
	fields,
	selectedFieldIndex,
	onSelectField,
	onAddField,
	onDeleteField,
	onReorderFields,
	onReceiveField,
	columnId,
	isReadOnly = false,
}: FieldsSidebarProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const { getComponent } = useComponentRegistry();

	const getFieldDisplayIcon = (field: FieldConfiguration) => {
		switch (field.displayStyle) {
			case 'line_break':
				return faBars;
			case 'text':
				return faFont;
			case 'badge':
			case 'chip':
				return faTag;
			case 'pill':
				return faEllipsis;
			case 'dot':
				return faCircle;
			case 'icon':
				return faIcons;
			default:
				return faFont;
		}
	};

	const getFieldDisplayName = (field: FieldConfiguration) => {
		if (field.displayStyle === 'line_break') {
			return '--- Line Break ---';
		}
		// Custom components show their display name from the registry
		if (field.displayStyle === 'custom' && field.customComponent?.id != null) {
			const component = getComponent(field.customComponent.id);
			return component?.displayName ?? field.customComponent.id;
		}
		// Get data source value
		const dataConfig = field.dataConfig;
		let sourceName = '';
		switch (dataConfig?.type) {
			case 'field':
				sourceName = dataConfig.field?.fieldName || 'Select field';
				break;
			case 'custom_text':
				sourceName = dataConfig.customText || 'Custom text';
				break;
			case 'template':
				sourceName = dataConfig.template ? 'Template' : 'Configure template';
				break;
		}

		// Combine display style and source
		const styleLabel =
			field.displayStyle.charAt(0).toUpperCase() + field.displayStyle.slice(1);
		return `${styleLabel}: ${sourceName}`;
	};

	const getFieldPreviewStyle = (field: FieldConfiguration) => {
		// Line breaks have minimal style
		if (field.displayStyle === 'line_break') {
			return {
				backgroundColor: 'transparent',
				color: colors.values.textSecondary,
			};
		}

		// Check if styleConfig and colorRule exist
		if (!field.styleConfig || !field.styleConfig.colorRule) {
			return {
				backgroundColor: colors.values.backgroundSecondary,
				color: colors.values.textPrimary,
			};
		}

		const { colorRule } = field.styleConfig;

		if (colorRule.type === 'static' && colorRule.staticColor) {
			return {
				backgroundColor: colorRule.staticColor.bg || '#e5e7eb',
				color: colorRule.staticColor.text || '#374151',
			};
		}

		// For value/range mapping, show a default style
		return {
			backgroundColor: '#e5e7eb',
			color: '#374151',
		};
	};

	return (
		<div
			className={`w-[220px] border-r flex flex-col h-full ${colors.classes.borderDefault} ${colors.classes.bgSecondary}`}
		>
			<div className={`p-4 border-b ${colors.classes.borderDefault}`}>
				<div className="flex justify-between items-center">
					<h3
						className={`text-base font-semibold ${colors.classes.textPrimary}`}
					>
						{t('Fields')}
					</h3>
					{!isReadOnly && (
						<Button onClick={onAddField} variant="primary" size="sm">
							<Icon icon={faPlus} size="xs" className="mr-1" />
							{t('Add')}
						</Button>
					)}
				</div>
			</div>

			<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
				<DraggableList
					className="!min-h-0"
					data={fields}
					keyExtractor={(field) => field.id}
					dragDataExtractor={(field) =>
						JSON.stringify({ field, sourceColumnId: columnId })
					}
					listId={`fields-sidebar-${columnId}`}
					allowInternalReordering={!isReadOnly}
					allowPositionalDrops={!isReadOnly}
					acceptDropFrom={isReadOnly ? [] : ['fields-sidebar-']} // Accept from any fields-sidebar-* list
					onDrop={(dragData, targetIndex) => {
						try {
							const { field, sourceColumnId } = JSON.parse(dragData);
							// Only handle if it's from a different column
							if (sourceColumnId !== columnId && onReceiveField) {
								onReceiveField(field, targetIndex);
							}
						} catch (e) {
							// Handle legacy format or errors
							// Error: Failed to parse drag data:
						}
					}}
					renderItem={(field, index, isDragging) => (
						<div
							onClick={() => onSelectField(index)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onSelectField(index);
								}
							}}
							role="button"
							tabIndex={0}
							className={cn(
								'flex items-center min-w-0 p-3 mx-2 my-1 rounded-md border transition-all cursor-pointer',
								selectedFieldIndex === index
									? colors.classes.buttonGroupSelected
									: isDragging
										? colors.classes.bgSecondary
										: `${colors.classes.bgPrimary} border-transparent hover:border-strong`,
							)}
						>
							{!isReadOnly && <DragHandle className="shrink-0 mr-1" />}

							{field.displayStyle !== 'line_break' && (
								<FontAwesomeIcon
									icon={getFieldDisplayIcon(field)}
									size="sm"
									className={cn(
										'shrink-0 mr-2',
										selectedFieldIndex !== index &&
											colors.classes.textSecondary,
									)}
								/>
							)}

							<TruncatedFieldLabel
								label={getFieldDisplayName(field)}
								isSelected={selectedFieldIndex === index}
								field={field}
								previewStyle={getFieldPreviewStyle(field)}
								colors={colors}
							/>

							{selectedFieldIndex === index && (
								<FontAwesomeIcon
									icon={faChevronRight}
									size="xs"
									className={`shrink-0 ${colors.classes.textInfo} mr-2`}
								/>
							)}

							{!isReadOnly && (
								<button
									onClick={(e) => {
										e.stopPropagation();
										onDeleteField(index);
									}}
									className={`shrink-0 ml-1 p-1 ${colors.classes.bgPrimaryHover} rounded`}
								>
									<FontAwesomeIcon
										icon={faTrash}
										size="xs"
										className={colors.classes.textSecondary}
									/>
								</button>
							)}
						</div>
					)}
					onReorder={onReorderFields}
				/>
			</div>
		</div>
	);
}
